import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-compliance-staff',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="p-5 min-h-full bg-[#F7FAFC] overflow-y-auto">
      <div class="max-w-[1500px] mx-auto">
        <div class="flex items-center justify-between gap-4 mb-5 shrink-0">
          <div>
            <h1 class="text-xl font-semibold text-slate-900 tracking-tight">Staff Profiles</h1>
            <p class="text-slate-500 text-xs">Manage access, field credentials, induction, and compliance records.</p>
          </div>
          <button (click)="openCreateModal()" class="h-10 px-4 text-white rounded-lg text-xs font-semibold shadow-sm transition-opacity hover:opacity-90 flex items-center gap-2 cursor-pointer" style="background:linear-gradient(135deg,#2B9AD2,#1A6FA6);">
            <i class="ti ti-user-plus text-base"></i> Add Staff Member
          </button>
        </div>

        <div class="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-5">
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Total Staff</p>
            <p class="text-xl font-semibold text-slate-900 mt-1">{{ staffList().length }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Active Accounts</p>
            <p class="text-xl font-semibold text-emerald-700 mt-1">{{ activeCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Pending Invites</p>
            <p class="text-xl font-semibold text-amber-700 mt-1">{{ invitedCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Licence Alerts</p>
            <p class="text-xl font-semibold text-rose-700 mt-1">{{ licenceAlertCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">Visa Alerts</p>
            <p class="text-xl font-semibold text-indigo-700 mt-1">{{ visaAlertCount() }}</p>
          </div>
          <div class="bg-white rounded-xl border border-[#E7EEF5] px-4 py-3">
            <p class="text-[10px] uppercase tracking-widest font-bold text-slate-400">App Ready</p>
            <p class="text-xl font-semibold text-[#1A6FA6] mt-1">{{ appReadyCount() }}</p>
          </div>
        </div>

        <div class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm mb-4">
          <div class="px-4 py-3 flex flex-wrap items-center gap-3">
            <div class="flex gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1">
              <button type="button" (click)="activeTab.set('office')" class="h-8 px-3 rounded-md text-xs font-semibold transition-colors cursor-pointer"
                      [class.bg-white]="activeTab() === 'office'" [class.text-[#1A6FA6]]="activeTab() === 'office'" [class.shadow-sm]="activeTab() === 'office'"
                      [class.text-slate-500]="activeTab() !== 'office'">
                Office <span class="ml-1 text-[10px] text-slate-400">{{ officeStaffCount() }}</span>
              </button>
              <button type="button" (click)="activeTab.set('field')" class="h-8 px-3 rounded-md text-xs font-semibold transition-colors cursor-pointer"
                      [class.bg-white]="activeTab() === 'field'" [class.text-[#1A6FA6]]="activeTab() === 'field'" [class.shadow-sm]="activeTab() === 'field'"
                      [class.text-slate-500]="activeTab() !== 'field'">
                Field Workers <span class="ml-1 text-[10px] text-slate-400">{{ fieldStaffCount() }}</span>
              </button>
            </div>

            <div class="relative flex-1 min-w-[220px] max-w-md">
              <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
              <input type="text" placeholder="Search name, email, phone, licence, visa..." [ngModel]="searchQuery()" (ngModelChange)="searchQuery.set($event)"
                     class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:outline-none focus:bg-white focus:border-[#1A6FA6] placeholder:text-slate-400">
            </div>

            <select [ngModel]="statusFilter()" (ngModelChange)="statusFilter.set($event)" class="h-9 px-3 rounded-lg text-xs border border-slate-200 bg-slate-50 text-slate-600 focus:outline-none cursor-pointer">
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="invited">Invited</option>
              <option value="licence">Licence Alert</option>
              <option value="visa">Visa Alert</option>
              <option value="induction">Induction Missing</option>
              <option value="app_ready">App Ready</option>
              <option value="app_attention">App Attention</option>
            </select>
          </div>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4">
          @if (isLoading()) {
            @for (n of skeletons; track n) {
              <div class="bg-white rounded-xl border border-slate-200 p-4 animate-pulse">
                <div class="flex items-center gap-3 mb-4">
                  <div class="w-11 h-11 rounded-lg bg-slate-100"></div>
                  <div class="flex-1">
                    <div class="h-4 w-32 bg-slate-100 rounded mb-2"></div>
                    <div class="h-3 w-20 bg-slate-50 rounded"></div>
                  </div>
                </div>
                <div class="h-20 bg-slate-50 rounded-lg mb-4"></div>
                <div class="h-9 bg-slate-100 rounded-lg"></div>
              </div>
            }
          } @else {
            @for (staff of filteredStaff(); track staff.id) {
              <div class="bg-white rounded-xl border border-slate-200 shadow-sm p-4 hover:shadow-md hover:-translate-y-0.5 transition-all min-w-0">
                <div class="flex items-start justify-between gap-3 mb-4">
                  <div class="flex items-center gap-3 min-w-0">
                    <div class="w-11 h-11 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center text-sm font-semibold shrink-0 overflow-hidden">
                      @if (staff.profile_photo_url) {
                        <img [src]="staff.profile_photo_url" [alt]="staff.name" class="w-full h-full object-cover">
                      } @else {
                        {{ staffInitial(staff) }}
                      }
                    </div>
                    <div class="min-w-0">
                      <h3 class="text-sm font-semibold text-slate-950 truncate">{{ staff.name }}</h3>
                      <p class="text-xs text-[#1A6FA6] font-semibold truncate">{{ staff.roleLabel }}</p>
                    </div>
                  </div>
                  <button type="button" (click)="toggleActiveStatus(staff)" class="px-2.5 py-1 rounded-md text-[10px] font-semibold border transition-colors" [ngClass]="statusBadgeClass(staff)">
                    {{ accountLabel(staff) }}
                  </button>
                </div>

                <div class="space-y-2 text-xs text-slate-600 mb-4">
                  <p class="flex items-center gap-2 min-w-0"><i class="ti ti-mail text-slate-400"></i><span class="truncate">{{ staff.email }}</span></p>
                  <p class="flex items-center gap-2 min-w-0"><i class="ti ti-phone text-slate-400"></i><span class="truncate">{{ staff.phone || 'No phone recorded' }}</span></p>
                  <p class="flex items-center gap-2 min-w-0"><i class="ti ti-clock-dollar text-slate-400"></i><span>{{ staff.hourly_rate | currency:'NZD':'symbol':'1.2-2' }} / hr</span></p>
                </div>

                @if (hasFieldStaffAccess(staff)) {
                  <div class="grid grid-cols-3 gap-2 mb-4">
                    <div class="rounded-lg border px-3 py-2" [ngClass]="licenceTileClass(staff)">
                      <p class="text-[10px] uppercase tracking-widest font-bold opacity-70">Licence</p>
                      <p class="text-xs font-semibold mt-0.5">{{ licenceLabel(staff) }}</p>
                    </div>
                    <div class="rounded-lg border px-3 py-2" [ngClass]="inductionTileClass(staff)">
                      <p class="text-[10px] uppercase tracking-widest font-bold opacity-70">Induction</p>
                      <p class="text-xs font-semibold mt-0.5">{{ staff.induction_date ? (staff.induction_date | date:'d MMM y') : 'Missing' }}</p>
                    </div>
                    <div class="rounded-lg border px-3 py-2" [ngClass]="visaTileClass(staff)">
                      <p class="text-[10px] uppercase tracking-widest font-bold opacity-70">Visa</p>
                      <p class="text-xs font-semibold mt-0.5">{{ visaLabel(staff) }}</p>
                    </div>
                  </div>
                } @else {
                  <div class="grid grid-cols-3 gap-2 mb-4">
                    <div class="rounded-lg border px-3 py-2 bg-slate-50 text-slate-600 border-slate-100">
                      <p class="text-[10px] uppercase tracking-widest font-bold opacity-70">Role</p>
                      <p class="text-xs font-semibold mt-0.5">{{ staff.roleLabel }}</p>
                    </div>
                    <div class="rounded-lg border px-3 py-2" [ngClass]="staff.is_activated ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-amber-50 text-amber-700 border-amber-100'">
                      <p class="text-[10px] uppercase tracking-widest font-bold opacity-70">Access</p>
                      <p class="text-xs font-semibold mt-0.5">{{ staff.is_activated ? 'Activated' : 'Invited' }}</p>
                    </div>
                    <div class="rounded-lg border px-3 py-2" [ngClass]="visaTileClass(staff)">
                      <p class="text-[10px] uppercase tracking-widest font-bold opacity-70">Visa</p>
                      <p class="text-xs font-semibold mt-0.5">{{ visaLabel(staff) }}</p>
                    </div>
                  </div>
                }

                <div class="rounded-lg border px-3 py-2 mb-4" [ngClass]="appTileClass(staff)">
                  <div class="flex items-start justify-between gap-2">
                    <div class="min-w-0">
                      <p class="text-[10px] uppercase tracking-widest font-bold opacity-70">Mobile App</p>
                      <p class="text-xs font-semibold mt-0.5 truncate">{{ appStatusLabel(staff) }}</p>
                    </div>
                    <i class="ti text-base shrink-0" [ngClass]="appStatusIcon(staff)"></i>
                  </div>
                  <p class="text-[10px] opacity-75 mt-1 truncate">{{ appSeenText(staff) }}</p>
                </div>

                <div class="flex items-center justify-between gap-2 pt-3 border-t border-slate-100">
                  <button (click)="openProfile(staff)" class="h-8 px-3 bg-white border border-slate-200 text-slate-700 rounded-lg text-xs font-semibold hover:bg-slate-50 transition-colors flex items-center gap-1">
                    <i class="ti ti-eye"></i> Profile
                  </button>
                  <div class="flex flex-wrap justify-end gap-1.5">
                    <button (click)="sendInviteEmail(staff)" [disabled]="isInviteActionRunning(staff)" title="Send invite email" class="h-8 px-3 bg-amber-50 text-amber-700 border border-amber-100 rounded-lg text-xs font-semibold hover:bg-amber-100 transition-colors disabled:opacity-50 flex items-center gap-1">
                      <i class="ti" [ngClass]="isInviteActionRunning(staff) ? 'ti-loader animate-spin' : 'ti-mail-forward'"></i> Invite
                    </button>
                    <button (click)="openEditModal(staff)" class="h-8 px-3 bg-[#EAF5FA] border border-[#D5EBF6] text-[#1A6FA6] rounded-lg text-xs font-semibold hover:bg-[#D5EBF6] transition-colors flex items-center gap-1">
                      <i class="ti ti-pencil"></i> Edit
                    </button>
                  </div>
                </div>
              </div>
            } @empty {
              <div class="col-span-full bg-white rounded-xl p-12 text-center border border-dashed border-slate-300">
                <div class="w-16 h-16 bg-[#EAF5FA] text-[#1A6FA6] rounded-xl flex items-center justify-center mx-auto mb-4">
                  <i class="ti ti-users text-3xl"></i>
                </div>
                <h3 class="text-lg font-semibold text-slate-900">No staff found</h3>
                <p class="text-slate-500 mt-2 text-sm">Adjust the filters or invite a new team member.</p>
              </div>
            }
          }
        </div>

        @if (showCreateModal()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">Invite Staff Member</h2>
                  <p class="text-xs text-slate-500 mt-1">The account remains invited until the staff member activates their password.</p>
                </div>
                <button (click)="showCreateModal.set(false)" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"><i class="ti ti-x text-lg"></i></button>
              </div>

              <form #staffForm="ngForm" (ngSubmit)="onSubmit(staffForm)" class="flex flex-col overflow-hidden">
                <div class="p-5 overflow-y-auto space-y-5">
                  <div class="flex items-center gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                    <div class="w-16 h-16 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center text-lg font-semibold overflow-hidden shrink-0">
                      @if (createProfilePhotoUrl) {
                        <img [src]="createProfilePhotoUrl" alt="Staff profile preview" class="w-full h-full object-cover">
                      } @else {
                        <i class="ti ti-user text-2xl"></i>
                      }
                    </div>
                    <div class="min-w-0">
                      <p class="text-sm font-semibold text-slate-900">Profile photo</p>
                      <p class="text-xs text-slate-500 mt-0.5">Upload a clear headshot for staff cards and profiles.</p>
                      <div class="mt-2 flex flex-wrap gap-2">
                        <label class="h-8 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 cursor-pointer inline-flex items-center gap-2">
                          <i class="ti ti-upload"></i> Upload Photo
                          <input type="file" accept="image/*" class="hidden" (change)="onCreatePhotoSelected($event)">
                        </label>
                        @if (createProfilePhotoUrl) {
                          <button type="button" (click)="removeCreatePhoto()" class="h-8 px-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs font-semibold">Remove</button>
                        }
                      </div>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Full Name</span>
                      <input type="text" name="name" ngModel required class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Email</span>
                      <input type="email" name="email" ngModel required class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Role</span>
                      <select name="role" [(ngModel)]="createRole" required class="field-input">
                        <option value="Admin">Administrator</option>
                        <option value="Dispatcher">Dispatcher</option>
                        <option value="Worker">Plumber / Field Worker</option>
                        <option value="Drainlayer">Drainlayer</option>
                        <option value="Excavator Operator">Excavator Operator</option>
                      </select>
                    </label>
                    @if (!isWorkerRole(createRole)) {
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Field Staff Access</span>
                      <label class="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                        <input type="checkbox" name="can_work_as_worker" [(ngModel)]="createCanWorkAsWorker" class="rounded border-slate-300">
                        Can be assigned field work
                      </label>
                    </label>
                    }
                    @if (createRole === 'Admin') {
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Leave Approval</span>
                      <label class="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                        <input type="checkbox" name="can_approve_leave" [(ngModel)]="createCanApproveLeave" class="rounded border-slate-300">
                        Can approve leave and receive approval emails
                      </label>
                    </label>
                    }
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Hourly Rate</span>
                      <input type="number" name="hourly_rate" ngModel min="0" step="0.5" class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Mobile Phone</span>
                      <input type="text" name="phone" ngModel class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Address</span>
                      <input type="text" name="address" ngModel class="field-input">
                    </label>
                  </div>

                  <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                    @if (isWorkerRole(createRole) || createCanWorkAsWorker) {
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Plumber Licence</span>
                      <input type="text" name="licence_plumber" ngModel class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Gasfitter Licence</span>
                      <input type="text" name="licence_gasfitter" ngModel class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Drainlayer Licence</span>
                      <input type="text" name="licence_drainlayer" ngModel class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Licence Expiry</span>
                      <input type="date" name="licence_expiry_date" ngModel class="field-input">
                    </label>
                    }
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">H&S Induction Date</span>
                      <input type="date" name="induction_date" ngModel class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Conduct Verified Date</span>
                      <input type="date" name="conduct_verified_at" ngModel class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Emergency Contact Name</span>
                      <input type="text" name="emergency_contact_name" ngModel class="field-input">
                    </label>
                    <label class="block">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Emergency Contact Phone</span>
                      <input type="text" name="emergency_contact_phone" ngModel class="field-input">
                    </label>
                    <label class="block md:col-span-2">
                      <span class="block text-xs font-semibold text-slate-600 mb-1.5">Notes</span>
                      <textarea name="notes" ngModel rows="3" class="field-input h-auto py-2 resize-none"></textarea>
                    </label>
                  </div>
                </div>

                <div class="p-5 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
                  <button type="button" (click)="showCreateModal.set(false)" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200">Cancel</button>
                  <button type="submit" [disabled]="!staffForm.valid || isSaving()" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50 flex items-center gap-2">
                    @if (isSaving()) { <i class="ti ti-loader animate-spin"></i> Sending... } @else { Send Invite }
                  </button>
                </div>
              </form>
            </div>
          </div>
        }

        @if (editingStaff()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">Edit Staff Profile</h2>
                  <p class="text-xs text-slate-500 mt-1">{{ editingStaff()?.name }}</p>
                </div>
                <button (click)="editingStaff.set(null)" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"><i class="ti ti-x text-lg"></i></button>
              </div>

              <div class="p-5 overflow-y-auto space-y-5">
                <div class="flex items-center gap-4 rounded-xl border border-slate-100 bg-slate-50 p-4">
                  <div class="w-16 h-16 rounded-lg bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center text-lg font-semibold overflow-hidden shrink-0">
                    @if (edit.profile_photo_url) {
                      <img [src]="edit.profile_photo_url" alt="Staff profile preview" class="w-full h-full object-cover">
                    } @else {
                      {{ staffInitial(edit) }}
                    }
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-semibold text-slate-900">Profile photo</p>
                    <p class="text-xs text-slate-500 mt-0.5">Shown on staff cards and the profile view.</p>
                    <div class="mt-2 flex flex-wrap gap-2">
                      <label class="h-8 px-3 rounded-lg bg-white border border-slate-200 text-slate-700 text-xs font-semibold hover:bg-slate-50 cursor-pointer inline-flex items-center gap-2">
                        <i class="ti ti-upload"></i> Change Photo
                        <input type="file" accept="image/*" class="hidden" (change)="onEditPhotoSelected($event)">
                      </label>
                      @if (edit.profile_photo_url) {
                        <button type="button" (click)="removeEditPhoto()" class="h-8 px-3 rounded-lg bg-rose-50 border border-rose-100 text-rose-700 text-xs font-semibold">Remove</button>
                      }
                    </div>
                  </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Full Name</span><input type="text" [(ngModel)]="edit.name" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Email</span><input type="email" [(ngModel)]="edit.email" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Role</span><select [(ngModel)]="edit.role" (ngModelChange)="onEditRoleChange($event)" class="field-input"><option value="Admin">Administrator</option><option value="Dispatcher">Dispatcher</option><option value="Worker">Plumber / Field Worker</option><option value="Drainlayer">Drainlayer</option><option value="Excavator Operator">Excavator Operator</option></select></label>
                  @if (!isWorkerRole(edit.role)) {
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Field Staff Access</span>
                    <label class="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                      <input type="checkbox" [(ngModel)]="edit.can_work_as_worker" class="rounded border-slate-300">
                      Can be assigned field work
                    </label>
                  </label>
                  }
                  @if (edit.role === 'Admin') {
                  <label class="block">
                    <span class="block text-xs font-semibold text-slate-600 mb-1.5">Leave Approval</span>
                    <label class="h-10 px-3 rounded-lg border border-slate-200 bg-slate-50 flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
                      <input type="checkbox" [(ngModel)]="edit.can_approve_leave" class="rounded border-slate-300">
                      Can approve leave and receive approval emails
                    </label>
                  </label>
                  }
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Hourly Rate</span><input type="number" [(ngModel)]="edit.hourly_rate" min="0" step="0.5" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Mobile Phone</span><input type="text" [(ngModel)]="edit.phone" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Address</span><input type="text" [(ngModel)]="edit.address" class="field-input"></label>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-100">
                  @if (isFieldCapableRole(edit)) {
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Plumber Licence</span><input type="text" [(ngModel)]="edit.licence_plumber" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Gasfitter Licence</span><input type="text" [(ngModel)]="edit.licence_gasfitter" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Drainlayer Licence</span><input type="text" [(ngModel)]="edit.licence_drainlayer" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Licence Expiry</span><input type="date" [(ngModel)]="edit.licence_expiry_date" class="field-input"></label>
                  }
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">H&S Induction Date</span><input type="date" [(ngModel)]="edit.induction_date" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Conduct Verified Date</span><input type="date" [(ngModel)]="edit.conduct_verified_at" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Emergency Contact Name</span><input type="text" [(ngModel)]="edit.emergency_contact_name" class="field-input"></label>
                  <label class="block"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Emergency Contact Phone</span><input type="text" [(ngModel)]="edit.emergency_contact_phone" class="field-input"></label>
                  <label class="block md:col-span-2"><span class="block text-xs font-semibold text-slate-600 mb-1.5">Notes</span><textarea [(ngModel)]="edit.notes" rows="3" class="field-input h-auto py-2 resize-none"></textarea></label>
                </div>

              </div>

              <div class="p-5 border-t border-slate-100 bg-slate-50 flex flex-wrap justify-between gap-2">
                <button (click)="sendInviteEmail(editingStaff())" [disabled]="isInviteActionRunning(editingStaff())" class="px-4 py-2 bg-amber-50 text-amber-700 border border-amber-100 text-sm font-semibold rounded-lg hover:bg-amber-100 disabled:opacity-50 flex items-center gap-2">
                  <i class="ti" [ngClass]="isInviteActionRunning(editingStaff()) ? 'ti-loader animate-spin' : 'ti-mail-forward'"></i> Send Invite Email
                </button>
                <div class="flex gap-2">
                  <button (click)="editingStaff.set(null)" class="px-4 py-2 text-slate-600 text-sm font-semibold rounded-lg hover:bg-slate-200">Cancel</button>
                  <button (click)="saveEdit()" [disabled]="isSaving() || !edit.name.trim()" class="px-5 py-2 bg-[#1A6FA6] text-white text-sm font-semibold rounded-lg hover:bg-[#0C3A5A] disabled:opacity-50 flex items-center gap-2">
                    @if (isSaving()) { <i class="ti ti-loader animate-spin"></i> Saving... } @else { Save Changes }
                  </button>
                </div>
              </div>
            </div>
          </div>
        }

        @if (selectedStaff()) {
          <div class="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div class="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden border border-slate-100">
              <div class="p-5 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between">
                <div>
                  <h2 class="text-lg font-semibold text-slate-900">Staff Profile</h2>
                  <p class="text-xs text-slate-500 mt-1">NZP-STAF-{{ selectedStaff()?.id }}</p>
                </div>
                <div class="flex gap-2">
                  <button (click)="openEditFromProfile()" class="px-4 py-2 bg-[#EAF5FA] text-[#1A6FA6] border border-[#D5EBF6] text-xs font-semibold rounded-lg hover:bg-[#D5EBF6]"><i class="ti ti-pencil"></i> Edit</button>
                  <button (click)="selectedStaff.set(null)" class="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-200 text-slate-500"><i class="ti ti-x text-lg"></i></button>
                </div>
              </div>

              <div class="p-5 overflow-y-auto">
                <div class="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5">
                  <aside class="bg-slate-50 rounded-xl border border-slate-200 p-4">
                    <div class="w-20 h-20 rounded-xl bg-[#EAF5FA] text-[#1A6FA6] flex items-center justify-center text-2xl font-semibold mb-4 overflow-hidden">
                      @if (selectedStaff()?.profile_photo_url) {
                        <img [src]="selectedStaff()?.profile_photo_url" [alt]="selectedStaff()?.name" class="w-full h-full object-cover">
                      } @else {
                        {{ staffInitial(selectedStaff()) }}
                      }
                    </div>
                    <h3 class="text-xl font-semibold text-slate-950">{{ selectedStaff()?.name }}</h3>
                    <p class="text-sm font-semibold text-[#1A6FA6] mt-1">{{ selectedStaff()?.roleLabel }}</p>
                    <div class="mt-4 flex flex-wrap gap-2">
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="statusBadgeClass(selectedStaff())">{{ accountLabel(selectedStaff()) }}</span>
                      @if (hasFieldStaffAccess(selectedStaff())) {
                        <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="licenceTileClass(selectedStaff())">{{ licenceLabel(selectedStaff()) }}</span>
                      }
                      <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="visaTileClass(selectedStaff())">{{ visaLabel(selectedStaff()) }}</span>
                    </div>
                    <button (click)="toggleActiveStatus(selectedStaff())" class="mt-5 w-full h-9 rounded-lg text-xs font-semibold border transition-colors" [ngClass]="selectedStaff()?.is_active ? 'bg-white text-slate-600 border-slate-200 hover:bg-red-50 hover:text-red-600' : 'bg-emerald-50 text-emerald-700 border-emerald-100 hover:bg-emerald-100'">
                      {{ selectedStaff()?.is_active ? 'Deactivate Account' : 'Activate Account' }}
                    </button>
                    <button (click)="sendInviteEmail(selectedStaff())" [disabled]="isInviteActionRunning(selectedStaff())" class="mt-2 w-full h-9 rounded-lg text-xs font-semibold border transition-colors bg-amber-50 text-amber-700 border-amber-100 hover:bg-amber-100 disabled:opacity-50">
                      @if (isInviteActionRunning(selectedStaff())) { Sending... } @else { Send Invite Email }
                    </button>
                  </aside>

                  <section class="grid grid-cols-1 xl:grid-cols-2 gap-4">
                    <div class="detail-panel">
                      <p class="panel-title">Contact</p>
                      <p><span>Email</span>{{ selectedStaff()?.email }}</p>
                      <p><span>Phone</span>{{ selectedStaff()?.phone || 'Not recorded' }}</p>
                      <p><span>Address</span>{{ selectedStaff()?.address || 'Not recorded' }}</p>
                      <p><span>Emergency</span>{{ emergencyText(selectedStaff()) }}</p>
                    </div>
                    <div class="detail-panel">
                      <p class="panel-title">Account</p>
                      <p><span>Hourly Rate</span>{{ selectedStaff()?.hourly_rate | currency:'NZD':'symbol':'1.2-2' }}</p>
                      <p><span>Leave Approval</span>{{ selectedStaff()?.can_approve_leave ? 'Can approve leave' : 'No approval access' }}</p>
                      <p><span>Invited</span>{{ selectedStaff()?.invited_at ? (selectedStaff()?.invited_at | date:'d MMM y') : 'Not recorded' }}</p>
                      <p><span>Activated</span>{{ selectedStaff()?.activated_at ? (selectedStaff()?.activated_at | date:'d MMM y') : 'Pending' }}</p>
                      <p><span>Last Login</span>{{ selectedStaff()?.last_login_at ? (selectedStaff()?.last_login_at | date:'d MMM y, h:mma') : 'Not recorded' }}</p>
                      <p><span>Mobile App</span>{{ appStatusLabel(selectedStaff()) }}</p>
                      <p><span>Last App Seen</span>{{ selectedStaff()?.app_status?.last_seen_at ? (selectedStaff()?.app_status?.last_seen_at | date:'d MMM y, h:mma') : 'Not recorded' }}</p>
                      <p><span>Last Location</span>{{ selectedStaff()?.app_status?.last_location_at ? (selectedStaff()?.app_status?.last_location_at | date:'d MMM y, h:mma') : 'Not recorded' }}</p>
                    </div>
                    @if (hasFieldStaffAccess(selectedStaff())) {
                    <div class="detail-panel xl:col-span-2">
                      <p class="panel-title">Licences</p>
                      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div class="licence-box"><span>Plumber</span><strong>{{ selectedStaff()?.licence_plumber || 'Not recorded' }}</strong></div>
                        <div class="licence-box"><span>Gasfitter</span><strong>{{ selectedStaff()?.licence_gasfitter || 'Not recorded' }}</strong></div>
                        <div class="licence-box"><span>Drainlayer</span><strong>{{ selectedStaff()?.licence_drainlayer || 'Not recorded' }}</strong></div>
                      </div>
                      <p class="mt-3"><span>Expiry</span>{{ selectedStaff()?.licence_expiry_date ? (selectedStaff()?.licence_expiry_date | date:'d MMM y') : 'Not recorded' }}</p>
                    </div>
                    }
                    <div class="detail-panel">
                      <p class="panel-title">Compliance</p>
                      <p><span>H&S Induction</span>{{ selectedStaff()?.induction_date ? (selectedStaff()?.induction_date | date:'d MMM y') : 'Not recorded' }}</p>
                      <p><span>Conduct Verified</span>{{ selectedStaff()?.conduct_verified_at ? (selectedStaff()?.conduct_verified_at | date:'d MMM y') : 'Not recorded' }}</p>
                    </div>
                    <div class="detail-panel xl:col-span-2">
                      <div class="flex items-center justify-between gap-3 mb-3">
                        <p class="panel-title mb-0">Immigration</p>
                        <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold border" [ngClass]="visaTileClass(selectedStaff())">{{ visaLabel(selectedStaff()) }}</span>
                      </div>
                      @if (selectedStaff()?.current_visa) {
                        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                          <div class="licence-box"><span>Visa Type</span><strong>{{ selectedStaff()?.current_visa?.visa_type }}</strong></div>
                          <div class="licence-box"><span>Visa Number</span><strong>{{ maskedVisaNumber(selectedStaff()?.current_visa) }}</strong></div>
                          <div class="licence-box"><span>Expiry</span><strong>{{ selectedStaff()?.current_visa?.visa_expiry_date ? (selectedStaff()?.current_visa?.visa_expiry_date | date:'d MMM y') : 'Not recorded' }}</strong></div>
                        </div>
                        <p class="mt-3"><span>Verified</span>{{ selectedStaff()?.current_visa?.verified_at ? ((selectedStaff()?.current_visa?.verified_at | date:'d MMM y') + ' by ' + (selectedStaff()?.current_visa?.verified_by_name || 'Admin')) : 'Not verified' }}</p>
                        @if (selectedStaff()?.current_visa?.work_rights_notes) {
                          <p><span>Conditions</span>{{ selectedStaff()?.current_visa?.work_rights_notes }}</p>
                        }
                        @if (selectedStaff()?.current_visa?.file_url) {
                          <p><span>Visa Copy</span><a [href]="visaFileUrl(selectedStaff()?.current_visa)" target="_blank" rel="noopener" class="text-[#1A6FA6] hover:underline">Open copy</a></p>
                        }
                      } @else {
                        <p class="block-text">No visa record saved.</p>
                      }
                      @if (selectedStaff()?.visas?.length > 1) {
                        <div class="mt-4 pt-3 border-t border-slate-100 space-y-2">
                          @for (visa of selectedStaff()?.visas; track visa.id) {
                            @if (visa.id !== selectedStaff()?.current_visa?.id) {
                              <div class="flex flex-wrap items-center justify-between gap-2 text-xs bg-slate-50 rounded-lg px-3 py-2">
                                <span class="font-semibold text-slate-700">{{ visa.visa_type }}</span>
                                <span class="text-slate-500">{{ visa.visa_expiry_date ? (visa.visa_expiry_date | date:'d MMM y') : 'No expiry' }}</span>
                              </div>
                            }
                          }
                        </div>
                      }
                    </div>
                    <div class="detail-panel">
                      <p class="panel-title">Notes</p>
                      <p class="block-text">{{ selectedStaff()?.notes || 'No staff notes recorded.' }}</p>
                    </div>
                  </section>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [`
    .field-input {
      width: 100%;
      height: 2.5rem;
      border-radius: 0.5rem;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      padding: 0 0.75rem;
      font-size: 0.875rem;
      color: #1e293b;
      outline: none;
      transition: border-color 160ms ease, background 160ms ease;
    }
    .field-input:focus {
      border-color: #1A6FA6;
      background: #fff;
    }
    .detail-panel {
      border: 1px solid #e2e8f0;
      border-radius: 0.75rem;
      background: #fff;
      padding: 1rem;
    }
    .panel-title {
      margin-bottom: 0.75rem;
      font-size: 0.625rem;
      font-weight: 700;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      color: #94a3b8;
    }
    .detail-panel p:not(.panel-title) {
      display: flex;
      justify-content: space-between;
      gap: 1rem;
      padding: 0.45rem 0;
      border-bottom: 1px solid #f1f5f9;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #334155;
    }
    .detail-panel p:last-child {
      border-bottom: 0;
    }
    .detail-panel span,
    .licence-box span {
      color: #94a3b8;
      font-weight: 700;
      font-size: 0.6875rem;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .licence-box {
      border: 1px solid #eef2f7;
      border-radius: 0.625rem;
      background: #f8fafc;
      padding: 0.75rem;
    }
    .licence-box strong {
      display: block;
      margin-top: 0.35rem;
      font-size: 0.875rem;
      color: #0f172a;
    }
    .detail-panel .block-text {
      display: block;
      line-height: 1.55;
    }
  `]
})
export class AdminComplianceStaffComponent implements OnInit {
  private api = inject(ApiService);

  staffList = signal<any[]>([]);
  isLoading = signal(true);
  activeTab = signal<'office' | 'field'>('office');
  searchQuery = signal('');
  statusFilter = signal('');
  skeletons = [1, 2, 3, 4, 5, 6];
  showCreateModal = signal(false);
  selectedStaff = signal<any | null>(null);
  editingStaff = signal<any | null>(null);
  isSaving = signal(false);
  isSavingVisa = signal(false);
  isResending = signal<number | null>(null);
  isConvertingInvite = signal<number | null>(null);

  createRole = 'Worker';
  createCanWorkAsWorker = false;
  createCanApproveLeave = false;
  createProfilePhotoUrl = '';
  createProfilePhotoFile: File | null = null;
  editProfilePhotoFile: File | null = null;
  editPhotoChanged = false;
  edit: any = {};
  editingVisaId: number | null = null;
  visaCopyFile: File | null = null;
  visaCopyFileName = '';
  visaForm: any = this.emptyVisaForm();
  visaTypes = [
    'NZ Citizen / No visa required',
    'Resident Visa',
    'Permanent Resident Visa',
    'Accredited Employer Work Visa',
    'Post Study Work Visa',
    'Working Holiday Visa',
    'Specific Purpose Work Visa',
    'Partner of Worker Work Visa',
    'Partner of New Zealander Work/Resident Visa',
    'Student Visa with work rights',
    'Interim Visa',
    'Other'
  ];

  officeStaffCount = computed(() => this.staffList().filter(staff => !this.isWorkerRole(staff.rawRole)).length);
  fieldStaffCount = computed(() => this.staffList().filter(staff => this.hasFieldStaffAccess(staff)).length);
  activeCount = computed(() => this.staffList().filter(staff => staff.is_activated && staff.is_active).length);
  invitedCount = computed(() => this.staffList().filter(staff => !staff.is_activated).length);
  licenceAlertCount = computed(() => this.staffList().filter(staff => this.hasLicenceAlert(staff)).length);
  visaAlertCount = computed(() => this.staffList().filter(staff => this.hasVisaAlert(staff)).length);
  appReadyCount = computed(() => this.staffList().filter(staff => this.hasFieldStaffAccess(staff) && this.isAppReady(staff)).length);

  filteredStaff = computed(() => {
    const query = this.searchQuery().trim().toLowerCase();
    const status = this.statusFilter();
    return this.staffList().filter(staff => {
      const tabMatches = this.activeTab() === 'field' ? this.hasFieldStaffAccess(staff) : !this.isWorkerRole(staff.rawRole);
      const queryMatches = !query || [
        staff.name, staff.email, staff.phone, staff.roleLabel,
        staff.licence_plumber, staff.licence_gasfitter, staff.licence_drainlayer,
        staff.current_visa?.visa_type, staff.current_visa?.visa_number, staff.app_status?.label
      ].some(value => String(value || '').toLowerCase().includes(query));
      const statusMatches = !status
        || (status === 'active' && staff.is_activated && staff.is_active)
        || (status === 'inactive' && staff.is_activated && !staff.is_active)
        || (status === 'invited' && !staff.is_activated)
        || (status === 'licence' && this.hasLicenceAlert(staff))
        || (status === 'visa' && this.hasVisaAlert(staff))
        || (status === 'induction' && !staff.induction_date)
        || (status === 'app_ready' && this.isAppReady(staff))
        || (status === 'app_attention' && this.needsAppAttention(staff));
      return tabMatches && queryMatches && statusMatches;
    });
  });

  ngOnInit() {
    this.loadStaff();
  }

  loadStaff() {
    this.isLoading.set(true);
    this.api.getStaff().subscribe({
      next: (res: any) => {
        const users = res.success && res.data?.users ? res.data.users : [];
        const mapped = users.map((u: any) => ({
          ...u,
          phone: u.phone || '',
          roleLabel: u.role === 'Worker' ? 'Plumber / Field Worker' : (Number(u.can_work_as_worker || u.has_worker_access) ? `${u.role} + Field` : u.role),
          rawRole: u.role,
          can_work_as_worker: !!Number(u.can_work_as_worker || u.has_worker_access),
          can_approve_leave: this.truthyFlag(u.can_approve_leave),
          hourly_rate: Number(u.hourly_rate || 0),
          is_active: !!Number(u.is_active),
          is_activated: !!Number(u.is_activated),
          visas: Array.isArray(u.visas) ? u.visas : [],
          current_visa: u.current_visa || null,
          app_status: u.app_status || null
        }));
        this.staffList.set(mapped);
        const selected = this.selectedStaff();
        if (selected?.id) {
          this.selectedStaff.set(mapped.find((staff: any) => Number(staff.id) === Number(selected.id)) || null);
        }
        const editing = this.editingStaff();
        if (editing?.id) {
          const fresh = mapped.find((staff: any) => Number(staff.id) === Number(editing.id)) || null;
          this.editingStaff.set(fresh);
          if (fresh) {
            this.edit = { ...this.edit, visas: fresh.visas, current_visa: fresh.current_visa };
          }
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.isLoading.set(false);
        showToast('error', 'Failed to load staff accounts.');
      }
    });
  }

  openCreateModal() {
    this.createRole = 'Worker';
    this.createCanWorkAsWorker = false;
    this.createCanApproveLeave = false;
    this.createProfilePhotoUrl = '';
    this.createProfilePhotoFile = null;
    this.showCreateModal.set(true);
  }

  openProfile(staff: any) {
    this.selectedStaff.set(staff);
  }

  openEditFromProfile() {
    const staff = this.selectedStaff();
    if (!staff) return;
    this.selectedStaff.set(null);
    this.openEditModal(staff);
  }

  openEditModal(staff: any) {
    this.editingStaff.set(staff);
    this.resetVisaForm(staff.current_visa || null);
    this.editProfilePhotoFile = null;
    this.editPhotoChanged = false;
    this.edit = {
      id: staff.id,
      name: staff.name || '',
      email: staff.email || '',
      role: staff.rawRole || staff.role || 'Worker',
      can_work_as_worker: !!staff.can_work_as_worker,
      can_approve_leave: this.truthyFlag(staff.can_approve_leave),
      phone: staff.phone || '',
      profile_photo_url: staff.profile_photo_url || '',
      hourly_rate: staff.hourly_rate || 0,
      address: staff.address || '',
      emergency_contact_name: staff.emergency_contact_name || '',
      emergency_contact_phone: staff.emergency_contact_phone || '',
      licence_plumber: staff.licence_plumber || '',
      licence_gasfitter: staff.licence_gasfitter || '',
      licence_drainlayer: staff.licence_drainlayer || '',
      licence_expiry_date: staff.licence_expiry_date || '',
      induction_date: staff.induction_date || '',
      conduct_verified_at: staff.conduct_verified_at || '',
      notes: staff.notes || ''
    };
  }

  saveEdit() {
    if (!this.editingStaff() || !this.edit.name.trim()) return;
    this.isSaving.set(true);
    const isWorker = this.isFieldCapableRole(this.edit);
    const payload: any = {
      id: this.edit.id,
      name: this.edit.name.trim(),
      email: this.edit.email.trim().toLowerCase(),
      role: this.edit.role,
      can_work_as_worker: this.isWorkerRole(this.edit.role) || this.edit.can_work_as_worker ? 1 : 0,
      can_approve_leave: this.edit.role === 'Admin' && this.edit.can_approve_leave ? 1 : 0,
      phone: this.edit.phone.trim(),
      hourly_rate: this.edit.hourly_rate || 0,
      address: this.edit.address || '',
      emergency_contact_name: this.edit.emergency_contact_name || '',
      emergency_contact_phone: this.edit.emergency_contact_phone || '',
      licence_plumber: isWorker ? (this.edit.licence_plumber || '') : '',
      licence_gasfitter: isWorker ? (this.edit.licence_gasfitter || '') : '',
      licence_drainlayer: isWorker ? (this.edit.licence_drainlayer || '') : '',
      licence_expiry_date: isWorker ? (this.edit.licence_expiry_date || '') : '',
      induction_date: this.edit.induction_date || '',
      conduct_verified_at: this.edit.conduct_verified_at || '',
      notes: this.edit.notes || '',
      change_photo: 0
    };
    this.api.updateStaff(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          if (this.editPhotoChanged) {
            this.api.updateStaffPhoto(this.edit.id, this.editProfilePhotoFile, !this.editProfilePhotoFile).subscribe({
              next: (photoRes: any) => this.finishEditSave(photoRes),
              error: (err) => {
                this.isSaving.set(false);
                showToast('error', err?.error?.message || 'Profile details saved, but the photo could not be updated.');
                this.loadStaff();
              }
            });
            return;
          }
          this.finishEditSave(res);
        } else {
          this.isSaving.set(false);
          showToast('error', res.message || 'Failed to update profile.');
        }
      },
      error: () => {
        this.isSaving.set(false);
        showToast('error', 'Network error while saving changes.');
      }
    });
  }

  private finishEditSave(res: any) {
    this.isSaving.set(false);
    if (res.success) {
          const savedUser = res.data?.user;
          if (savedUser?.id) {
            const canApproveLeave = this.truthyFlag(savedUser.can_approve_leave);
            this.staffList.update(list => list.map(staff => Number(staff.id) === Number(savedUser.id) ? { ...staff, can_approve_leave: canApproveLeave } : staff));
            if (this.selectedStaff()?.id && Number(this.selectedStaff()?.id) === Number(savedUser.id)) {
              this.selectedStaff.update(staff => ({ ...staff, can_approve_leave: canApproveLeave }));
            }
          }
          showToast('success', 'Staff profile updated.');
          this.editingStaff.set(null);
          this.loadStaff();
    } else showToast('error', res.message || 'Failed to update profile photo.');
  }

  onSubmit(form: NgForm) {
    if (!form.valid) return;
    this.isSaving.set(true);
    const payload = this.sanitiseStaffPayload({
      ...form.value,
      role: this.createRole,
      can_work_as_worker: this.isWorkerRole(this.createRole) || this.createCanWorkAsWorker ? 1 : 0,
      can_approve_leave: this.createRole === 'Admin' && this.createCanApproveLeave ? 1 : 0
    });
    this.api.createStaff(payload).subscribe({
      next: (res: any) => {
        if (res.success) {
          const finishCreate = () => {
            this.isSaving.set(false);
          if (res.data?.email_sent === false) {
            showToast('error', res.message || 'Staff profile created, but activation email was not sent.');
          } else {
            showToast('success', res.message || 'Staff member invited.');
          }
          this.showCreateModal.set(false);
          this.createRole = 'Worker';
          this.createCanWorkAsWorker = false;
          this.createCanApproveLeave = false;
          this.createProfilePhotoUrl = '';
          this.createProfilePhotoFile = null;
          form.resetForm({ role: 'Worker' });
          this.loadStaff();
          };
          if (this.createProfilePhotoFile && res.data?.id) {
            this.api.updateStaffPhoto(Number(res.data.id), this.createProfilePhotoFile).subscribe({
              next: finishCreate,
              error: (err) => {
                this.isSaving.set(false);
                showToast('error', err?.error?.message || 'Staff created, but the profile photo could not be uploaded.');
                this.loadStaff();
              }
            });
          } else finishCreate();
        } else {
          this.isSaving.set(false);
          showToast('error', res.message || 'Failed to add staff.');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err?.error?.message || 'Network error while adding staff.');
      }
    });
  }

  toggleActiveStatus(staff: any) {
    if (!staff?.is_activated) {
      showToast('error', 'Invited staff must activate their account first.');
      return;
    }
    const newStatus = !staff.is_active;
    this.api.updateStaff({ id: staff.id, is_active: newStatus ? 1 : 0 }).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', `${staff.name} is now ${newStatus ? 'active' : 'inactive'}.`);
          this.loadStaff();
          if (this.selectedStaff()?.id === staff.id) {
            this.selectedStaff.update(s => ({ ...s, is_active: newStatus }));
          }
        } else {
          showToast('error', res.message || 'Failed to update status.');
        }
      },
      error: () => showToast('error', 'Network error.')
    });
  }

  resendInvite(staff: any) {
    if (!staff) return;
    this.isResending.set(staff.id);
    this.api.resendActivation(staff.id).subscribe({
      next: (res: any) => {
        this.isResending.set(null);
        if (res.success) {
          if (res.data?.email_sent === false) {
            showToast('error', res.message || `Activation link was not sent to ${staff.email}.`);
          } else {
            showToast('success', res.message || `Activation link sent to ${staff.email}.`);
          }
          this.loadStaff();
        } else {
          showToast('error', res.message || 'Failed to resend invite.');
        }
      },
      error: () => {
        this.isResending.set(null);
        showToast('error', 'Network error.');
      }
    });
  }

  sendInviteEmail(staff: any) {
    if (!staff) return;
    if (staff.is_activated) {
      this.convertToInvite(staff);
      return;
    }
    this.resendInvite(staff);
  }

  isInviteActionRunning(staff: any): boolean {
    const staffId = staff?.id;
    return !!staffId && (this.isResending() === staffId || this.isConvertingInvite() === staffId);
  }

  convertToInvite(staff: any) {
    if (!staff?.id || !staff.is_activated) return;
    const confirmed = window.confirm(`Send a new invite email to ${staff.name}? Their current login access will be revoked until they activate again.`);
    if (!confirmed) return;

    this.isConvertingInvite.set(staff.id);
    this.api.convertStaffToInvite(staff.id).subscribe({
      next: (res: any) => {
        this.isConvertingInvite.set(null);
        if (res.success) {
          if (res.data?.email_sent === false) {
            showToast('error', res.message || `Invite email was not sent to ${staff.email}.`);
          } else {
            showToast('success', res.message || `Invite email sent to ${staff.email}.`);
          }
          this.editingStaff.set(null);
          this.selectedStaff.set(null);
          this.loadStaff();
        } else {
          showToast('error', res.message || 'Failed to convert account.');
        }
      },
      error: (err) => {
        this.isConvertingInvite.set(null);
        showToast('error', err?.error?.message || 'Network error.');
      }
    });
  }

  staffInitial(staff: any): string {
    return String(staff?.name || 'S').trim().charAt(0).toUpperCase();
  }

  onCreatePhotoSelected(event: Event) {
    this.readPhotoFile(event, value => this.createProfilePhotoUrl = value, file => this.createProfilePhotoFile = file);
  }

  onEditPhotoSelected(event: Event) {
    this.readPhotoFile(event, value => this.edit.profile_photo_url = value, file => {
      this.editProfilePhotoFile = file;
      this.editPhotoChanged = true;
    });
  }

  removeCreatePhoto() {
    this.createProfilePhotoUrl = '';
    this.createProfilePhotoFile = null;
  }

  removeEditPhoto() {
    this.edit.profile_photo_url = '';
    this.editProfilePhotoFile = null;
    this.editPhotoChanged = true;
  }

  onVisaCopySelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] || null;
    if (!file) return;

    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      showToast('error', 'Visa copy must be a PDF, JPG, PNG, or WebP file.');
      input.value = '';
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      showToast('error', 'Visa copy must be under 8MB.');
      input.value = '';
      return;
    }

    this.visaCopyFile = file;
    this.visaCopyFileName = file.name;
    this.visaForm.remove_file = false;
  }

  saveVisa() {
    const staff = this.editingStaff();
    if (!staff?.id || !this.visaForm.visa_type) return;

    this.isSavingVisa.set(true);
    const formData = new FormData();
    if (this.editingVisaId) {
      formData.append('id', String(this.editingVisaId));
    }
    formData.append('user_id', String(staff.id));
    formData.append('visa_type', this.visaForm.visa_type || '');
    formData.append('visa_number', this.visaForm.visa_number || '');
    formData.append('visa_expiry_date', this.visaForm.visa_expiry_date || '');
    formData.append('work_rights_notes', this.visaForm.work_rights_notes || '');
    formData.append('notes', this.visaForm.notes || '');
    formData.append('is_current', this.visaForm.is_current ? '1' : '0');
    formData.append('verified', this.visaForm.verified ? '1' : '0');
    formData.append('remove_file', this.visaForm.remove_file ? '1' : '0');
    if (this.visaCopyFile) {
      formData.append('visa_copy', this.visaCopyFile);
    }

    this.api.saveStaffVisa(formData).subscribe({
      next: (res: any) => {
        this.isSavingVisa.set(false);
        if (res.success) {
          showToast('success', 'Visa record saved.');
          this.resetVisaForm();
          this.loadStaff();
        } else {
          showToast('error', res.message || 'Failed to save visa record.');
        }
      },
      error: (err) => {
        this.isSavingVisa.set(false);
        showToast('error', err?.error?.message || 'Network error while saving visa record.');
      }
    });
  }

  editVisaRecord(visa: any) {
    this.resetVisaForm(visa);
  }

  deleteVisaRecord(visa: any) {
    if (!visa?.id) return;
    const confirmed = window.confirm(`Delete this ${visa.visa_type || 'visa'} record?`);
    if (!confirmed) return;

    this.api.deleteStaffVisa(Number(visa.id)).subscribe({
      next: (res: any) => {
        if (res.success) {
          showToast('success', 'Visa record deleted.');
          this.resetVisaForm();
          this.loadStaff();
        } else {
          showToast('error', res.message || 'Failed to delete visa record.');
        }
      },
      error: (err) => showToast('error', err?.error?.message || 'Network error while deleting visa record.')
    });
  }

  resetVisaForm(visa: any = null) {
    this.editingVisaId = visa?.id ? Number(visa.id) : null;
    this.visaCopyFile = null;
    this.visaCopyFileName = '';
    this.visaForm = visa ? {
      visa_type: visa.visa_type || this.visaTypes[0],
      visa_number: visa.visa_number || '',
      visa_expiry_date: visa.visa_expiry_date || '',
      file_url: visa.file_url || '',
      work_rights_notes: visa.work_rights_notes || '',
      notes: visa.notes || '',
      is_current: Number(visa.is_current) === 1,
      verified: !!visa.verified_at,
      remove_file: false
    } : this.emptyVisaForm();
  }

  private emptyVisaForm(): any {
    return {
      visa_type: 'NZ Citizen / No visa required',
      visa_number: '',
      visa_expiry_date: '',
      file_url: '',
      work_rights_notes: '',
      notes: '',
      is_current: true,
      verified: false,
      remove_file: false
    };
  }

  isWorkerRole(role: any): boolean {
    return ['Worker', 'Drainlayer', 'Excavator Operator'].includes(String(role || ''));
  }

  private truthyFlag(value: any): boolean {
    return value === true || value === 1 || String(value ?? '').toLowerCase() === '1' || String(value ?? '').toLowerCase() === 'true';
  }

  onEditRoleChange(role: string) {
    if (this.isWorkerRole(role)) {
      this.edit.can_work_as_worker = true;
      this.edit.can_approve_leave = false;
      return;
    }

    if (role !== 'Admin') {
      this.edit.can_approve_leave = false;
    }

    if (!this.edit.can_work_as_worker) {
      this.edit.licence_plumber = '';
      this.edit.licence_gasfitter = '';
      this.edit.licence_drainlayer = '';
      this.edit.licence_expiry_date = '';
    }
  }

  private sanitiseStaffPayload(payload: any): any {
    delete payload.profile_photo_url;
    payload.can_work_as_worker = this.isWorkerRole(payload.role) || payload.can_work_as_worker ? 1 : 0;
    payload.can_approve_leave = payload.role === 'Admin' && payload.can_approve_leave ? 1 : 0;
    if (!this.isWorkerRole(payload.role) && !payload.can_work_as_worker) {
      payload.licence_plumber = '';
      payload.licence_gasfitter = '';
      payload.licence_drainlayer = '';
      payload.licence_expiry_date = '';
    }
    return payload;
  }

  hasFieldStaffAccess(staff: any): boolean {
    return this.isWorkerRole(staff?.rawRole || staff?.role) || !!staff?.can_work_as_worker || !!staff?.has_worker_access;
  }

  isFieldCapableRole(staff: any): boolean {
    return this.isWorkerRole(staff?.role || staff?.rawRole) || !!staff?.can_work_as_worker;
  }

  private readPhotoFile(event: Event, assign: (value: string) => void, assignFile: (file: File) => void) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      showToast('error', 'Please choose an image file.');
      input.value = '';
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      showToast('error', 'Profile photo must be under 2MB.');
      input.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      assign(String(reader.result || ''));
      assignFile(file);
    };
    reader.onerror = () => showToast('error', 'Could not read profile photo.');
    reader.readAsDataURL(file);
  }

  accountLabel(staff: any): string {
    if (!staff?.is_activated) return 'Invited';
    return staff.is_active ? 'Active' : 'Inactive';
  }

  appStatusLabel(staff: any): string {
    return staff?.app_status?.label || (staff?.last_login_at ? 'Logged in, app not seen' : 'No app login');
  }

  appSeenText(staff: any): string {
    const status = staff?.app_status;
    if (!staff?.is_activated) return 'Waiting for activation';
    if (!staff?.last_login_at) return 'Worker has not completed OTP login';
    if (!status?.last_seen_at) return 'No mobile app heartbeat received';
    const seen = new Date(status.last_seen_at);
    if (Number.isNaN(seen.getTime())) return 'App heartbeat recorded';
    const minutes = Math.floor((Date.now() - seen.getTime()) / 60000);
    if (minutes < 2) return 'Seen just now';
    if (minutes < 60) return `Seen ${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `Seen ${hours} hr ago`;
    const days = Math.floor(hours / 24);
    return `Seen ${days} day${days === 1 ? '' : 's'} ago`;
  }

  isAppReady(staff: any): boolean {
    return !!staff?.app_status?.is_installed && !!staff?.app_status?.last_seen_at;
  }

  needsAppAttention(staff: any): boolean {
    if (!this.hasFieldStaffAccess(staff)) return false;
    const status = String(staff?.app_status?.status || '');
    return ['activated_never_logged_in', 'logged_in_no_app_ping', 'browser_only', 'installed_inactive', 'invited'].includes(status);
  }

  appStatusIcon(staff: any): string {
    const status = String(staff?.app_status?.status || '');
    if (status === 'installed') return 'ti-device-mobile-check';
    if (status === 'browser_only') return 'ti-world';
    if (status === 'installed_inactive') return 'ti-device-mobile-question';
    if (status === 'activated_never_logged_in' || status === 'logged_in_no_app_ping') return 'ti-alert-circle';
    return 'ti-device-mobile';
  }

  appTileClass(staff: any): string {
    const status = String(staff?.app_status?.status || '');
    if (status === 'installed') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    if (status === 'browser_only') return 'bg-sky-50 text-sky-700 border-sky-100';
    if (status === 'installed_inactive') return 'bg-amber-50 text-amber-700 border-amber-100';
    if (status === 'activated_never_logged_in' || status === 'logged_in_no_app_ping') return 'bg-rose-50 text-rose-700 border-rose-100';
    return 'bg-slate-50 text-slate-600 border-slate-100';
  }

  statusBadgeClass(staff: any): string {
    if (!staff?.is_activated) return 'bg-amber-50 text-amber-700 border-amber-100';
    if (staff.is_active) return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    return 'bg-slate-50 text-slate-500 border-slate-200';
  }

  hasLicenceAlert(staff: any): boolean {
    if (!this.hasFieldStaffAccess(staff)) return false;
    if (!staff.licence_expiry_date) return true;
    const days = this.daysUntil(staff.licence_expiry_date);
    return days <= 60;
  }

  licenceLabel(staff: any): string {
    if (!this.hasFieldStaffAccess(staff)) return 'Office';
    if (!staff.licence_expiry_date) return 'Missing';
    const days = this.daysUntil(staff.licence_expiry_date);
    if (days < 0) return 'Expired';
    if (days <= 60) return `${days} days`;
    return 'Current';
  }

  licenceTileClass(staff: any): string {
    if (!this.hasFieldStaffAccess(staff)) return 'bg-slate-50 text-slate-600 border-slate-100';
    if (!staff.licence_expiry_date) return 'bg-rose-50 text-rose-700 border-rose-100';
    const days = this.daysUntil(staff.licence_expiry_date);
    if (days < 0) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (days <= 60) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  inductionTileClass(staff: any): string {
    return staff?.induction_date
      ? 'bg-emerald-50 text-emerald-700 border-emerald-100'
      : 'bg-amber-50 text-amber-700 border-amber-100';
  }

  hasVisaAlert(staff: any): boolean {
    const visa = staff?.current_visa;
    if (!visa) return true;
    if (this.isNoVisaRequired(visa)) return false;
    if (!visa.visa_expiry_date) return true;
    const days = this.daysUntil(visa.visa_expiry_date);
    return days <= 60;
  }

  visaLabel(staff: any): string {
    const visa = staff?.current_visa;
    if (!visa) return 'Missing';
    if (this.isNoVisaRequired(visa)) return 'No visa';
    if (!visa.visa_expiry_date) return 'No expiry';
    const days = this.daysUntil(visa.visa_expiry_date);
    if (days < 0) return 'Expired';
    if (days <= 60) return `${days} days`;
    return 'Current';
  }

  visaTileClass(staff: any): string {
    const visa = staff?.current_visa;
    if (!visa) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (this.isNoVisaRequired(visa)) return 'bg-slate-50 text-slate-600 border-slate-100';
    if (!visa.visa_expiry_date) return 'bg-amber-50 text-amber-700 border-amber-100';
    const days = this.daysUntil(visa.visa_expiry_date);
    if (days < 0) return 'bg-rose-50 text-rose-700 border-rose-100';
    if (days <= 60) return 'bg-amber-50 text-amber-700 border-amber-100';
    return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  }

  maskedVisaNumber(visa: any): string {
    const value = String(visa?.visa_number || '').trim();
    if (!value) return 'Not recorded';
    if (value.length <= 4) return value;
    return `${'*'.repeat(Math.max(0, value.length - 4))}${value.slice(-4)}`;
  }

  visaFileUrl(visa: any): string {
    return this.api.assetUrl(visa?.file_url || '');
  }

  emergencyText(staff: any): string {
    if (!staff?.emergency_contact_name && !staff?.emergency_contact_phone) return 'Not recorded';
    return `${staff.emergency_contact_name || 'Contact'} ${staff.emergency_contact_phone || ''}`.trim();
  }

  private isNoVisaRequired(visa: any): boolean {
    return String(visa?.visa_type || '').toLowerCase().includes('no visa required')
      || String(visa?.visa_type || '').toLowerCase().includes('nz citizen');
  }

  private daysUntil(dateValue: string): number {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const target = new Date(dateValue);
    target.setHours(0, 0, 0, 0);
    return Math.ceil((target.getTime() - today.getTime()) / 86400000);
  }
}
